import React, { useState } from 'react';
import { X, Mic, MicOff, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { marketplaceLeads } from '../../services/dealerApi';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';

const SOURCES = [
  'CarDekho',
  'CarWale',
  'CarTrade',
  'OLX',
  'WhatsApp',
  'Walk-in',
  'Referral',
  'Other',
];

/**
 * ManualLeadModal — single-lead entry form with voice-to-text on every
 * text field (browser Web Speech API, zero server cost).
 *
 * Props:
 *   open        boolean — modal visibility
 *   onClose(created?) — fires with the fresh lead when saved
 */
export default function ManualLeadModal({ open, onClose }) {
  const [form, setForm] = useState({
    customer_name: '',
    mobile_number: '',
    source: 'Walk-in',
    interested_car: '',
    branch: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const { supported, listening, activeField, start, stop } = useSpeechRecognition({
    lang: 'en-IN',
    onFinal: (text, fieldKey) => {
      if (!fieldKey) return;
      setForm((f) => {
        const prev = (f[fieldKey] || '').trim();
        const next = prev ? `${prev} ${text}` : text;
        // For mobile, strip non-digits so a spoken number like "nine one one zero..." → digits
        if (fieldKey === 'mobile_number') {
          return { ...f, [fieldKey]: next.replace(/[^\d+]/g, '') };
        }
        return { ...f, [fieldKey]: next };
      });
    },
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleMic = (field) => {
    if (listening && activeField === field) stop();
    else start(field);
  };

  const canSubmit = form.customer_name.trim() && form.mobile_number.trim();

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await marketplaceLeads.createManual({
        customer_name: form.customer_name.trim(),
        mobile_number: form.mobile_number.trim(),
        source: form.source,
        interested_car: form.interested_car.trim() || null,
        branch: form.branch.trim() || null,
        notes: form.notes.trim() || null,
      });
      if (res.merged) {
        toast.success('Existing lead updated — details merged');
      } else {
        toast.success('Lead saved — call the customer next');
      }
      // Reset for the next entry
      setForm({
        customer_name: '',
        mobile_number: '',
        source: form.source,
        interested_car: '',
        branch: '',
        notes: '',
      });
      onClose?.(res.lead);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={() => !saving && onClose?.()}
      data-testid="manual-lead-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3 sticky top-0 bg-slate-900 z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">Manual Entry</div>
            <h3 className="text-xl font-extrabold text-white tracking-tight mt-1 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-400" /> Add Lead
            </h3>
            <div className="text-xs text-slate-400 mt-1">
              {supported ? 'Tap the mic on any field to dictate.' : 'Voice input not supported in this browser.'}
            </div>
          </div>
          <button
            onClick={() => onClose?.()}
            className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5"
            data-testid="manual-lead-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          <VoiceField
            label="Customer name *"
            testid="ml-name"
            value={form.customer_name}
            onChange={(v) => setField('customer_name', v)}
            supported={supported}
            listening={listening && activeField === 'customer_name'}
            onToggleMic={() => toggleMic('customer_name')}
            placeholder="e.g. Rakesh Kumar"
            autoFocus
          />

          <VoiceField
            label="Mobile number *"
            testid="ml-mobile"
            value={form.mobile_number}
            onChange={(v) => setField('mobile_number', v.replace(/[^\d+]/g, ''))}
            supported={supported}
            listening={listening && activeField === 'mobile_number'}
            onToggleMic={() => toggleMic('mobile_number')}
            placeholder="10-digit or +91..."
            inputMode="tel"
            type="tel"
          />

          <section>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-1.5">Source *</label>
            <select
              value={form.source}
              onChange={(e) => setField('source', e.target.value)}
              data-testid="ml-source"
              className="w-full px-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none"
            >
              {SOURCES.map((s) => (
                <option key={s} value={s} className="bg-slate-900">{s}</option>
              ))}
            </select>
          </section>

          <VoiceField
            label="Interested car"
            testid="ml-car"
            value={form.interested_car}
            onChange={(v) => setField('interested_car', v)}
            supported={supported}
            listening={listening && activeField === 'interested_car'}
            onToggleMic={() => toggleMic('interested_car')}
            placeholder="e.g. Hyundai Creta 2022"
          />

          <VoiceField
            label="Branch"
            testid="ml-branch"
            value={form.branch}
            onChange={(v) => setField('branch', v)}
            supported={supported}
            listening={listening && activeField === 'branch'}
            onToggleMic={() => toggleMic('branch')}
            placeholder="e.g. Whitefield"
          />

          <VoiceField
            label="Notes"
            testid="ml-notes"
            value={form.notes}
            onChange={(v) => setField('notes', v)}
            supported={supported}
            listening={listening && activeField === 'notes'}
            onToggleMic={() => toggleMic('notes')}
            placeholder="Budget, timeline, financing needs, any other context…"
            multiline
          />

          {/* Send WhatsApp confirmation has been moved to the Lead Update
              modal — telecaller calls first, and only triggers WhatsApp
              from the Update flow when the call doesn't connect. */}

          {listening && (
            <div className="text-xs text-cyan-300 flex items-center gap-2" data-testid="ml-listening-indicator">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
              </span>
              Listening… speak now
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-end gap-2 sticky bottom-0 bg-slate-900">
          <button
            onClick={() => onClose?.()}
            className="px-4 py-2 rounded-lg text-slate-400 hover:text-white text-sm"
            data-testid="ml-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit || saving}
            data-testid="ml-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Save lead
          </button>
        </div>
      </div>
    </div>
  );
}

function VoiceField({
  label, testid, value, onChange, placeholder, supported, listening, onToggleMic,
  multiline = false, autoFocus = false, inputMode, type = 'text',
}) {
  const commonCls =
    'w-full px-3 py-2.5 pr-11 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none';
  return (
    <section>
      <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-1.5">{label}</label>
      <div className="relative">
        {multiline ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={3}
            data-testid={testid}
            className={commonCls}
          />
        ) : (
          <input
            type={type}
            inputMode={inputMode}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            data-testid={testid}
            className={commonCls}
          />
        )}
        {supported && (
          <button
            type="button"
            onClick={onToggleMic}
            title={listening ? 'Stop dictation' : 'Dictate'}
            aria-label={listening ? 'Stop dictation' : 'Start dictation'}
            data-testid={`${testid}-mic`}
            className={`absolute right-1.5 top-1.5 p-2 rounded-md border transition-colors ${
              listening
                ? 'bg-red-500 border-red-400 text-white animate-pulse'
                : 'bg-slate-800/70 border-slate-700 text-cyan-300 hover:bg-slate-700'
            }`}
          >
            {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
      </div>
    </section>
  );
}
